import { Button, Flex, Stack, Text } from '@mantine/core';
import { closeModal, openModal } from '@mantine/modals';
import { useTranslation } from 'react-i18next';
import { Form, FormButton } from 'shared/ui/Form';
import { useRemoveNote } from '../hooks';

type ConfirmDeleteNoteBodyProps = {
  documentId: number;
  onClose: () => void;
  onDeleted: () => void;
};

const ConfirmDeleteNoteBody: React.FC<ConfirmDeleteNoteBodyProps> = ({
  documentId,
  onClose,
  onDeleted,
}) => {
  const { t } = useTranslation(['documents', 'common']);
  const { removeNote } = useRemoveNote(documentId);

  const handleSubmit = async () => {
    await removeNote();
    onDeleted();
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Stack spacing={16}>
        <Text>{t('data.notes.deleteMessage', { ns: 'documents' })}</Text>
        <Flex gap={8} justify="flex-end">
          <Button
            onClick={onClose}
            variant="default"
            data-testid="btn-cancel-delete-note"
          >
            {t('cancel', { ns: 'common' })}
          </Button>
          <FormButton
            type="submit"
            color="red"
            data-testid="btn-confirm-delete-note"
          >
            {t('yesDelete', { ns: 'common' })}
          </FormButton>
        </Flex>
      </Stack>
    </Form>
  );
};

const MODAL_ID = 'confirm-delete-note';

const Title = () => {
  const { t } = useTranslation(['documents']);
  return <>{t('data.notes.delete', { ns: 'documents' })}</>;
};

export const confirmDeleteNote = (documentId: number) =>
  new Promise<boolean>(resolve => {
    const close = () => {
      resolve(false);
    };

    openModal({
      modalId: MODAL_ID,
      title: <Title />,
      onClose: close,
      centered: true,
      children: (
        <ConfirmDeleteNoteBody
          documentId={documentId}
          onClose={() => closeModal(MODAL_ID)}
          onDeleted={() => {
            resolve(true);
            closeModal(MODAL_ID);
          }}
        />
      ),
    });
  });
